import React, { useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import RisingParticlesScene from './RisingParticlesScene';
import { DotLottiePlayer } from '@dotlottie/react-player';
import { motion, useScroll, useTransform } from 'framer-motion';

interface ProcessStep {
    id: number;
    phase: string;
    title: string;
    description: string;
    duration: string;
    deliverables: string[];
    icon: string;
    animation: string;
}

interface ProcessTimelineProps {
    onBookConsultation?: () => void;
}

const ProcessTimeline = ({ onBookConsultation }: ProcessTimelineProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start 50%", "end 50%"]
    });

    const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

    const processSteps: ProcessStep[] = [
        {
            id: 1,
            phase: 'Phase 1',
            title: 'Discovery & Audit',
            description: 'Comprehensive analysis of your current brand visibility, competitor landscape, and growth opportunities.',
            duration: '1-2 weeks',
            deliverables: [
                'Complete SEO audit report',
                'Google Business Profile analysis',
                'Competitor benchmarking',
                'Keyword opportunity mapping'
            ],
            icon: 'MagnifyingGlassCircleIcon',
            animation: '/animations/process-discovery.lottie'
        },
        {
            id: 2,
            phase: 'Phase 2',
            title: 'Strategy Development',
            description: 'Custom visibility strategy tailored to your business goals, target audience, and market positioning.',
            duration: '1 week',
            deliverables: [
                'SEO strategy roadmap',
                'GBP optimization plan',
                'Content calendar',
                'KPI framework'
            ],
            icon: 'DocumentChartBarIcon',
            animation: '/animations/process-strategy.lottie'
        },
        {
            id: 3,
            phase: 'Phase 3',
            title: 'Implementation',
            description: 'Execute optimization strategies across all channels with continuous monitoring and refinement.',
            duration: '4-6 weeks',
            deliverables: [
                'Technical SEO fixes',
                'GBP profile optimization',
                'Content creation and publishing',
                'Citation building'
            ],
            icon: 'WrenchScrewdriverIcon',
            animation: '/animations/process-implementation.lottie'
        },
        {
            id: 4,
            phase: 'Phase 4',
            title: 'Growth & Optimization',
            description: 'Ongoing monitoring, analysis, and optimization to maximize visibility and maintain competitive advantage.',
            duration: 'Ongoing',
            deliverables: [
                'Monthly performance reports',
                'Strategy refinements',
                'A/B testing results',
                'Quarterly strategy sessions'
            ],
            icon: 'ChartBarSquareIcon',
            animation: '/animations/process-growth.lottie'
        }
    ];

    return (
        <section className="py-32 bg-white w-full border-t border-slate-200 relative overflow-hidden">
            {/* Background Particles - Small Size */}
            <RisingParticlesScene size={0.06} />

            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">

                {/* Header - Audit Style */}
                <div className="mb-24 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-bold uppercase tracking-widest border border-slate-200">
                                Scope of Work
                            </div>
                        </div>
                        <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-slate-900 leading-[1] mb-6">
                            OPTIMIZATION<br />
                            <span className="text-indigo-600">ROADMAP.</span>
                        </h2>
                        <div className="w-24 h-1 bg-indigo-600"></div>
                    </div>
                    <p className="text-xl text-slate-500 max-w-xl leading-relaxed font-medium text-right">
                        Your clear, phase-by-phase execution plan for market dominance.
                    </p>
                </div>

                <div className="relative" ref={containerRef}>
                    {/* Central Line - Only visible on desktop */}
                    <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-slate-200 transform -translate-x-1/2" />

                    {/* Filling Line - Scroll Animated */}
                    <motion.div
                        style={{ height: lineHeight }}
                        className="hidden lg:block absolute left-1/2 top-0 w-px bg-indigo-600 transform -translate-x-1/2 origin-top z-10"
                    />

                    <div className="space-y-0">
                        {processSteps.map((step, index) => (
                            <div key={step.id} className="relative">
                                {/* Desktop: Alternating Layout */}
                                <div className={`grid lg:grid-cols-2 gap-16 lg:gap-32 items-center py-16 ${index !== processSteps.length - 1 ? 'border-b border-slate-100 lg:border-none' : ''}`}>

                                    {/* Left Content (or Right for even items on desktop) */}
                                    <div className={`${index % 2 === 1 ? 'lg:order-2' : 'lg:text-right'} group`}>
                                        <div className={`flex flex-col ${index % 2 === 1 ? 'items-start' : 'items-start lg:items-end'}`}>

                                            {/* Step Number - Massive */}
                                            <div className="text-8xl font-bold text-slate-100 group-hover:text-indigo-50 transition-colors duration-500 leading-none mb-4 -ml-2 lg:ml-0">
                                                {String(step.id).padStart(2, '0')}
                                            </div>

                                            <div className="inline-block px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-bold uppercase tracking-widest mb-4 border border-indigo-100 rounded-md">
                                                {step.phase}
                                            </div>

                                            <h3 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4 tracking-tight group-hover:text-indigo-600 transition-colors duration-300">
                                                {step.title}
                                            </h3>

                                            <p className="text-lg text-slate-500 mb-8 max-w-md">
                                                {step.description}
                                            </p>

                                            <ul className={`space-y-3 ${index % 2 === 1 ? 'text-left' : 'text-left lg:text-right'}`}>
                                                {step.deliverables.map((deliverable, idx) => (
                                                    <li key={idx} className={`flex items-center gap-3 text-slate-600 font-medium ${index % 2 === 1 ? 'justify-start' : 'justify-start lg:justify-end'}`}>
                                                        {/* Icon order flips based on alignment */}
                                                        {index % 2 === 0 && <span className="hidden lg:block">{deliverable}</span>}

                                                        <Icon name="CheckCircleIcon" size={18} className="text-indigo-600 flex-shrink-0" />

                                                        {(index % 2 === 1 || true) && <span className={index % 2 === 0 ? 'lg:hidden' : ''}>{deliverable}</span>}
                                                    </li>
                                                ))}
                                            </ul>

                                            <div className="mt-8 flex items-center gap-2 text-sm font-bold text-indigo-600 uppercase tracking-wider">
                                                <Icon name="ClockIcon" size={16} />
                                                <span>Duration: {step.duration}</span>
                                            </div>

                                        </div>
                                    </div>

                                    {/* Animation Visual Side */}
                                    <div className={`${index % 2 === 1 ? 'lg:order-1' : ''} hidden lg:block w-full h-full relative`}>
                                        <div className="relative w-full h-96 flex items-center justify-center">
                                            <DotLottiePlayer
                                                src={step.animation}
                                                autoplay
                                                loop
                                                className="w-full h-full object-contain"
                                            />
                                        </div>
                                    </div>

                                    {/* Center Dot */}
                                    <motion.div
                                        initial={{ borderColor: '#e2e8f0', backgroundColor: '#ffffff' }}
                                        whileInView={{ borderColor: '#4f46e5', backgroundColor: '#ffffff', scale: 1.2 }}
                                        viewport={{ margin: "-50% 0px -50% 0px" }}
                                        transition={{ duration: 0.4 }}
                                        className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 border-4 rounded-full z-20"
                                    />

                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom CTA Section */}
                <div className="mt-32 border-t border-slate-900 pt-16">
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-8">
                            <h3 className="text-4xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
                                Ready to Transform Your <br />
                                <span className="text-indigo-600">Brand Visibility?</span>
                            </h3>
                        </div>
                        <div className="lg:col-span-4 flex flex-col gap-6">
                            <p className="text-lg text-slate-500 font-medium">
                                Start your journey to dominating local search results and maximizing your online presence with our proven process.
                            </p>
                            <button
                                onClick={onBookConsultation}
                                className="self-start px-10 py-5 bg-slate-900 text-white font-bold text-sm uppercase tracking-widest hover:bg-indigo-600 transition-colors duration-300 cursor-pointer"
                            >
                                Get Started Today <span className="ml-2">→</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProcessTimeline;